import React, {useState} from 'react';
import {ExportOutlined} from '@ant-design/icons';
import {Button, Form, Modal, Popover, Select, Typography} from 'antd';
import {createD365Export} from '../../api/d365Api';
import TextArea from 'antd/es/input/TextArea';
import _ from 'lodash';
import {VALID_PROJECT_CODE} from "../Constants";

const ExportButton = ({
                        exportType,
                        includeDateFilter,
                        includeProjectCodes,
                        includeWorkerIds,
                        disabled,
                        onExport,
                        pendingProjectCodes,
                        style
                      }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form] = Form.useForm();

  const FORM_DATE_FILTER_NAME = 'Date Filter';
  const FORM_PROJECT_CODES_NAME = 'Project Codes';
  const FORM_WORKER_IDS_NAME = 'Worker IDs';

  const dateFilterOptions = [
    {label: 'This Month', value: 'THIS_MONTH'},
    {label: 'Last Month', value: 'LAST_MONTH'},
    {label: 'Last Two Months', value: 'LAST_TWO_MONTHS'},
    {label: 'Last Three Months', value: 'LAST_THREE_MONTHS'},
    {label: 'Last Four Months', value: 'LAST_FOUR_MONTHS'},
    {label: 'Last Five Months', value: 'LAST_FIVE_MONTHS'},
    {label: 'Last Six Months', value: 'LAST_SIX_MONTHS'},
    {label: 'Last Year', value: 'LAST_YEAR'},
  ];

  const textAreaToArray = v => {
    if (!v || v === '') {
      return [];
    } else {
      return v.split('\n');
    }
  };

  const _export = async () => {
    try {
      setLoading(true);
      const dateFilter = form.getFieldValue(FORM_DATE_FILTER_NAME);
      const projectCodes = textAreaToArray(form.getFieldValue(FORM_PROJECT_CODES_NAME));
      const workerIds = textAreaToArray(form.getFieldValue(FORM_WORKER_IDS_NAME));
      const response = await createD365Export(exportType, dateFilter, projectCodes, workerIds);
      onExport(exportType, response);
    } catch (e) {
      console.error(`Could not create D365 ${_.capitalize(exportType)} export`);
    } finally {
      setLoading(false);
      setShowModal(false);
      form.resetFields();
    }
  };


  const _validateProjectCodes = (value) => {
    let msg = ''
    if (value && value !== '') {
      let projectCodes = value.split(/\r?[\n,]/);
      const hasDuplicates = projectCodes.some((e,i) => {
        return projectCodes.indexOf(e) !== i;
      });
      if (hasDuplicates) {
        msg = 'Project Code list has duplicates';
      } else {
        _.each(projectCodes, pc => {
          if (pc !== '') {
            if (!VALID_PROJECT_CODE.test(pc)) {
              msg = 'Incorrect Project Code';
              return
            }
            if (pendingProjectCodes.includes(pc)) {
              msg = 'Contains pending Project Code';
            }
          } else {
            msg = 'Empty line or code not allowed';
          }
        })
      }
    } else {
      // when you have pending projects codes, a project code is required, since when left empty you
      // create an export to handle all Project Codes
      // For Hours Export it is not required, because Hours will automatically exclude pending project codes
      if (pendingProjectCodes.length > 0) {
        if (exportType === "RATES") {
          msg = "'Project Codes' is required";
        }
      }
    }
    return msg;
  }

  return (
    <>
      <Popover
        placement="topRight"
        trigger="hover,click,focus"
        content={
          <Typography>Only available when there are no pending exports, and are all fully loaded, when processing all
            Project Codes</Typography>
        }>
        <Button onClick={() => setShowModal(true)} loading={loading} disabled={disabled}
                icon={<ExportOutlined/>} style={style}>
          Export {_.capitalize(exportType)}
        </Button>
      </Popover>
      {showModal && (
        <Form
          name={`export${_.capitalize(exportType)}`}
          form={form}
          onFinish={_export}
          labelCol={{span: 6}}
        >
          <Modal
            title={`Export ${_.capitalize(exportType)}`}
            open={showModal}
            onOk={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
            footer={[
              <Button key="cancel" onClick={() => setShowModal(false)}>Cancel</Button>,
              <Button key="submit" type="primary" onClick={() => form.submit()} loading={loading}>Start
                Export</Button>
            ]}
          >
            {pendingProjectCodes.length > 0 && (
              <Typography style={{color: "gray", fontSize: "13px", marginBottom: "15px"}}>
                {exportType === "RATES" ?
                  (<p>There are pending project codes, making the Project Code(s) field required.</p>) :
                  (<p>Pending project codes [{pendingProjectCodes.join(", ")}] will be automatically excluded,
                      if you leave Project Code(s) field blank.
                    </p>
                  )}
              </Typography>
            )}
            {includeDateFilter && (
              <Form.Item
                label="Date Filter"
                name={FORM_DATE_FILTER_NAME}
                rules={[{required: true}]}
              >
                <Select options={dateFilterOptions} placeholder="Select a date filter"/>
              </Form.Item>
            )}
            {includeProjectCodes && (
              <Form.Item
                label="Project Code(s)"
                name={FORM_PROJECT_CODES_NAME}

                rules={[
                  () => ({
                    validator(_, value) {
                      const msg = _validateProjectCodes(value);
                      if (msg === '') {
                        return Promise.resolve();
                      } else {
                        return Promise.reject(msg);
                      }
                    }
                  })]}
              >
                <TextArea rows={4} placeholder="Project Code(s) - Please enter one per line"/>
              </Form.Item>
            )}
            {includeWorkerIds && (
              <Form.Item
                label="Worker ID(s)"
                name={FORM_WORKER_IDS_NAME}
                rules={[{required: false}]}
              >
                <TextArea rows={4} placeholder="Worker ID(s) - Please enter one per line"/>
              </Form.Item>
            )}
          </Modal>
        </Form>
      )}
    </>
  );
};

export {ExportButton};